import { sendMessageToApp } from './exsternalCall';
import { isDensePhoneLayout, isFoldableUa } from './deviceLayout';

const ANDROID_UA = /Android/i;
const MEASURE_PX = 100;
const APPLY_ABOVE = 1.04;
const MIN_INVERSE = 0.4;
const NATIVE_RETRY_MS = [0, 400, 1200, 3000];

export const inverseTextZoom = (zoom, { minInverse = MIN_INVERSE, applyAbove = APPLY_ABOVE } = {}) => {
  const scale = Number(zoom);
  if (!Number.isFinite(scale) || scale <= applyAbove) return 1;
  return Math.max(minInverse, 1 / scale);
};

export const pickInflationRatio = (samples) => {
  const values = (samples || [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (!values.length) return 1;
  return Math.max(1, ...values);
};

export const getHtmlZoom = () => {
  if (typeof document === 'undefined') return 1;
  const html = document.documentElement;
  const fromStyle = parseFloat(html.style.zoom);
  if (Number.isFinite(fromStyle) && fromStyle > 0) return fromStyle;
  const fromVar = parseFloat(html.style.getPropertyValue('--zb-css-zoom'));
  if (Number.isFinite(fromVar) && fromVar > 0) return fromVar;
  return 1;
};

const isLikelyWebView = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  // Solo Android: su iPad lo zoom/transform su html blocca lo scroll del calendario.
  return ANDROID_UA.test(navigator.userAgent || '');
};

/**
 * True solo per tablet “veri”. Su Fold/cover non saltare il font-lock:
 * altrimenti textZoom di sistema gonfia tutta la UI e taglia i CTA.
 */
const isTabletViewport = () => {
  if (typeof window === 'undefined') return false;
  if (isDensePhoneLayout()) return false;
  if (isFoldableUa()) return false;
  const width = window.innerWidth || 0;
  const height = window.innerHeight || 0;
  if (width < 840) return false;
  if (Math.min(width, height) < 600) return false;
  return true;
};

const readPx = (value, fallback) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * textZoom di Android a volte non cambia getComputedStyle().fontSize
 * (restituisce il px dichiarato) ma gonfia em / box del glifo.
 */
export const measureTextZoom = () => {
  if (typeof document === 'undefined') return 1;
  const html = document.documentElement;
  const previousZoom = html.style.zoom;
  const previousTransform = html.style.transform;
  html.style.zoom = '1';
  html.style.transform = 'none';

  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = [
    'position:absolute',
    'left:-9999px',
    'top:0',
    `width:${MEASURE_PX}px`,
    'height:auto',
    `font-size:${MEASURE_PX}px`,
    `line-height:${MEASURE_PX}px`,
    'padding:0',
    'margin:0',
    'border:0',
    'white-space:nowrap',
  ].join(';');
  probe.textContent = 'MMMMM';
  html.appendChild(probe);

  const computed = readPx(window.getComputedStyle(probe).fontSize, MEASURE_PX) / MEASURE_PX;
  const emBox = document.createElement('div');
  emBox.style.cssText = probe.style.cssText + ';width:1em;height:1em;font-size:100px;line-height:1;';
  html.appendChild(emBox);

  const emHeight = (emBox.offsetHeight || 0) / MEASURE_PX;
  const emWidth = (emBox.offsetWidth || 0) / MEASURE_PX;
  const glyphHeight = (probe.getBoundingClientRect().height || 0) / MEASURE_PX;

  emBox.remove();
  probe.remove();
  html.style.zoom = previousZoom;
  html.style.transform = previousTransform;

  return pickInflationRatio([computed, emHeight, emWidth, glyphHeight]);
};

const zoomRenders = () => {
  if (typeof document === 'undefined') return false;
  const el = document.createElement('div');
  el.style.cssText = 'position:absolute;left:-9999px;width:100px;height:20px;zoom:0.5;';
  document.documentElement.appendChild(el);
  const visual = el.getBoundingClientRect().width;
  el.remove();
  return visual > 0 && visual < 80;
};

/**
 * Su Fold/Samsung `zoom` + `width: 100%/zoom` allarga il layout senza
 * rimpicciolire altrettanto → CTA tagliati a destra. Preferiamo contain.
 */
export const pickFontLockMode = ({
  dense = false,
  zoomSupported = false,
} = {}) => {
  if (dense) return 'zoom-contain';
  if (zoomSupported) return 'zoom';
  return 'transform';
};

const clearLock = (html) => {
  html.removeAttribute('data-zb-font-lock');
  html.removeAttribute('data-zb-font-lock-mode');
  html.style.removeProperty('zoom');
  html.style.removeProperty('transform');
  html.style.removeProperty('transform-origin');
  html.style.removeProperty('--zb-css-zoom');
  html.style.removeProperty('--zb-text-zoom');
};

const applyLock = (html, inverse, zoom, mode) => {
  html.setAttribute('data-zb-font-lock', 'true');
  html.setAttribute('data-zb-font-lock-mode', mode);
  html.style.setProperty('--zb-text-zoom', String(zoom));
  html.style.setProperty('--zb-css-zoom', String(inverse));
  html.style.webkitTextSizeAdjust = 'none';
  html.style.textSizeAdjust = 'none';

  if (mode === 'transform') {
    html.style.removeProperty('zoom');
    html.style.transformOrigin = '0 0';
    html.style.transform = `scale(${inverse})`;
    return;
  }

  // zoom | zoom-contain
  html.style.removeProperty('transform');
  html.style.removeProperty('transform-origin');
  html.style.zoom = String(inverse);
};

const requestNativeTextZoom = () => {
  if (typeof window === 'undefined') return;
  if (!window.ReactNativeWebView && !localStorage.getItem('fromApp')) return;
  sendMessageToApp('app:setTextZoom', { value: 100 });
};

/**
 * Android WebView moltiplica i font (textZoom = dimensione sistema).
 * I layout in px (modal prenotazione, orari) traboccano.
 * Compensiamo con zoom CSS / transform e chiediamo all'app nativa textZoom=100.
 */
export const lockWebViewTextZoom = () => {
  if (typeof document === 'undefined' || !isLikelyWebView()) {
    return () => {};
  }

  const html = document.documentElement;
  let mode = null;

  const apply = () => {
    const zoom = measureTextZoom();
    const inverse = inverseTextZoom(zoom);
    // Tablet: zoom/transform su html blocca il pan del calendario (stesso bug iPad).
    if (inverse === 1 || isTabletViewport()) {
      clearLock(html);
      return;
    }
    if (!mode) {
      mode = pickFontLockMode({
        dense: isDensePhoneLayout(),
        zoomSupported: zoomRenders(),
      });
    } else if (isDensePhoneLayout() && mode !== 'zoom-contain') {
      // Resize cover ↔ inner: passa al mode sicuro senza width espansa.
      mode = 'zoom-contain';
    }
    applyLock(html, inverse, zoom, mode);
  };

  apply();
  NATIVE_RETRY_MS.forEach((delay) => {
    window.setTimeout(() => {
      requestNativeTextZoom();
      apply();
    }, delay);
  });

  const onChange = () => apply();
  window.addEventListener('resize', onChange);
  window.addEventListener('pageshow', onChange);
  document.addEventListener('visibilitychange', onChange);

  return () => {
    window.removeEventListener('resize', onChange);
    window.removeEventListener('pageshow', onChange);
    document.removeEventListener('visibilitychange', onChange);
  };
};
